import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ErrorConfig, ERROR_CODES } from '@/lib/http-client';
import { useAlert, useSignOut } from '@/hooks';
import { getErrorDisplayMessage, isErrorConfig, shouldRedirectAskHackettAuthorization } from '@/lib/utils';
import { RenderJson } from '@/components';

export function useHandleError() {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { signOutNoServiceCall } = useSignOut();
	const { showAlert } = useAlert();

	const handleError = useCallback(
		async (error: ErrorConfig | unknown) => {
			if (isErrorConfig(error)) {
				if (error.code === ERROR_CODES.REQUEST_ABORTED) {
					return;
				}

				if (error.status === 503) {
					navigate('/maintenance', { replace: true });
					return;
				}

				if (error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'NotAuthorizedException') {
					signOutNoServiceCall();
					return;
				}

				if (error.status === 401) {
					signOutNoServiceCall();
					return;
				}

				const displayMessage = getErrorDisplayMessage(error);

				if (error.status === 403) {
					showAlert({
						variant: 'danger',
						children: (
							<p className="mb-0 text-white" style={{ whiteSpace: 'pre-line' }}>
								{displayMessage}
							</p>
						),
					});

					if (shouldRedirectAskHackettAuthorization(error, pathname)) {
						navigate('/', { replace: true });
					}

					return;
				}

				if (error.code === 'WARNING') {
					showAlert({
						variant: 'warning',
						children: (
							<p className="mb-0 text-white" style={{ whiteSpace: 'pre-line' }}>
								{displayMessage}
							</p>
						),
					});
					return;
				}

				showAlert({
					variant: 'danger',
					children: (
						<p className="mb-0 text-white" style={{ whiteSpace: 'pre-line' }}>
							<strong>Error:</strong> {displayMessage}
						</p>
					),
				});
			} else {
				showAlert({
					variant: 'danger',
					children: <RenderJson json={error} />,
				});
			}
		},
		[navigate, pathname, showAlert, signOutNoServiceCall]
	);

	return handleError;
}
